import { Link } from "react-router-dom";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { useEffect, useState, useCallback, useMemo } from "react";
import { ProgressSpinner } from 'primereact/progressspinner';
import fetchMethodRequest from "../../../../config/service";
import config from "../../../../config/config";
import apiCalls from "../../../../config/apiCalls";

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { Users, UserCheck, UserPlus, File, FileText, Eye, HardDrive } from 'lucide-react';
// import HomeNews from "../../HomeNews/components/HomeNews";
import Loader from '../../../App/Loader';

const Dashboard = () => {
    const [dashboardData, setDashboardData] = useState(null);
    const [activityData, setActivityData] = useState([]);
    const [filesData, setFilesData] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {

        fetchData();
    }, []);

    // Simulating API calls with mock data
    const fetchData = async () => {
        try {
            // Parallel API calls - O(1) instead of O(3) sequential calls
            const [dashboard, activity, filesObj] = await Promise.all([
                fetchMethodRequest('GET', apiCalls.dashboardData),
                fetchMethodRequest('GET', apiCalls.activityGraph),
                fetchMethodRequest('GET', apiCalls.filesGraph)
            ]);

            // O(n) - only iterates once through the object keys
            const files = Object.entries(filesObj || {}).map(([userName, count]) => ({
                userName,
                count
            }));

            setDashboardData(dashboard);
            setActivityData(activity);
            setFilesData(files);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoading(false);
        }
    };

    const StatCard = ({ icon: Icon, title, value, subtitle, bgColor }) => (
        <div className="col">
            <div className="rounded h-100 shadow-sm">
                <div className="card-body p-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <div className={`p-2 rounded ${bgColor}`} style={{ width: '48px', height: '48px' }}>
                            <Icon size={32} className="text-white" />
                        </div>
                    </div>
                    <div className="text-muted small mb-1">{title}</div>
                    <div className="h3 fw-bold mb-0">{value}</div>
                    {subtitle && <div className="text-muted small mt-1">{subtitle}</div>}
                </div>
            </div>
        </div>
    );

    const AreaChartCard = ({ title, icon: Icon, data, xKey, yKey, gradientId, color }) => {
        if (!data || data.length === 0) return null;

        return (
            <div className="col-12 col-lg-6">
                <div className="shadow-sm">
                    <div className="card-body p-3">
                        <h5 className="card-title d-flex align-items-center mb-3">
                            <Icon size={20} className="text-primary me-2" />
                            {title}
                        </h5>

                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={data}>
                                <defs>
                                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="5%" stopColor={color} stopOpacity={0.3} />
                                        <stop offset="95%" stopColor={color} stopOpacity={0} />
                                    </linearGradient>
                                </defs>

                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis dataKey={xKey} stroke="#6b7280" />
                                <YAxis stroke="#6b7280" />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: '#fff',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '0.375rem',
                                    }}
                                />

                                <Area
                                    type="monotone"
                                    dataKey={yKey}
                                    stroke={color}
                                    strokeWidth={2}
                                    fill={`url(#${gradientId})`}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="container-fluid p-4" style={{
            backgroundColor: '#f8f9fa',
            // minHeight: '100vh' 
        }}>
            <h2 className="h3 fw-bold mb-4">Dashboard</h2>
            <Loader loader={loading} />

            <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
                {/* Stats Grid */}
                <div className="row row-cols-1 row-cols-md-3 row-cols-lg-6 g-3 mb-4 w-100">
                    <StatCard
                        icon={Users}
                        title="Total Users"
                        value={dashboardData?.totalUsers?.toLocaleString()}
                        bgColor="bg-primary"
                    />
                    <StatCard
                        icon={UserCheck}
                        title="Active Users"
                        value={dashboardData?.activeUsers?.toLocaleString()}
                        subtitle="Last 7 Days"
                        bgColor="bg-success"
                    />
                    <StatCard
                        icon={File}
                        title="Total Files"
                        value={dashboardData?.totalFiles?.toLocaleString()}
                        bgColor="bg-primary"
                    />
                </div>

                {/* Charts Grid */}
                <div className="row g-4">

                    {/* Files Uploaded Over Time */}
                    {filesData.length > 0 && <AreaChartCard
                        title="Files viewed by Users"
                        icon={File}
                        data={filesData}
                        xKey="userName"
                        yKey="count"
                        gradientId="colorFiles"
                        color="#0d6efd"
                    />}

                    {/* File Views Over Time */}
                    {activityData.length > 0 && <AreaChartCard
                        title="User Activities"
                        icon={Eye}
                        data={activityData}
                        xKey="userName"
                        yKey="count"
                        gradientId="colorViews"
                        color="#198754"
                    />}
                </div>

                {/*  */}
                {/* <HomeNews /> */}
            </div>
        </div>
    );
};

export default Dashboard;
